import type {ButtonHTMLAttributes, ReactNode} from 'react';
import {Link, type LinkProps} from 'react-router';

/**
 * Button variants (audit items #14, #44, #61, #78).
 * - primary: the one main action in a region (the established yellow button).
 * - secondary: any other action (default, so an unstyled button is never primary by accident).
 * - tertiary: low-emphasis text action.
 * - danger: destructive or irreversible action; pair with a confirmation.
 * - link: an inline action that reads as a link.
 */
export const BUTTON_VARIANTS=['primary','secondary','tertiary','danger','link'] as const;
export type ButtonVariant=(typeof BUTTON_VARIANTS)[number];

const variantClass:Record<ButtonVariant,string>={
  primary:'primary',
  secondary:'secondary',
  tertiary:'tertiary',
  danger:'danger',
  link:'link-button',
};

export function buttonClassName(variant:ButtonVariant='secondary',className?:string){
  return [variantClass[variant],className].filter(Boolean).join(' ');
}

export type ButtonProps=Omit<ButtonHTMLAttributes<HTMLButtonElement>,'className'>&{
  variant?:ButtonVariant;
  className?:string;
};

/** Never submits by accident: `type` defaults to "button". */
export function Button({variant='secondary',className,type='button',...props}:ButtonProps){
  return <button {...props} type={type} className={buttonClassName(variant,className)}/>;
}

export type SubmitButtonProps=Omit<ButtonProps,'type'|'children'>&{
  pending?:boolean;
  pendingLabel?:string;
  children:ReactNode;
};

/** Submit button that shows "Saving…" and stays disabled while a save is running. */
export function SubmitButton({pending=false,pendingLabel='Saving…',children,variant='primary',disabled,...props}:SubmitButtonProps){
  return <Button {...props} type="submit" variant={variant} disabled={disabled||pending} aria-busy={pending||undefined}>{pending?pendingLabel:children}</Button>;
}

export type ButtonLinkProps=Omit<LinkProps,'className'>&{
  variant?:ButtonVariant;
  className?:string;
};

/** A navigation link that looks like a button. */
export function ButtonLink({variant='secondary',className,...props}:ButtonLinkProps){
  return <Link {...props} className={['button-link',buttonClassName(variant,className)].join(' ')}/>;
}
